import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TablePagination,
  Select,
  MenuItem,
  Divider,
  Button,
} from "@mui/material";
import BondTable from "./BondTable";
import { usePortfolioRebalanceStore } from "../../Store/portfolioRebalanceStore";
import { getBondTypeOptions, getSecurityChargeOptions } from "../../http/api";
import { orderBondTypeOptions } from "../OrderEntry/optionsHelper";
import { useAuth } from "../../Context/AuthProvider";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ZeroPositionEntryModal from "./ZeroPositionEntryModal";
import { tokens } from "../../Common/designTokens"; // added

const PortfolioRebalanceTable = ({
  data,
  selectedColumns,
  allColumns,
  loading,
  sortConfig,
  onSort,
  page,
  itemsPerPage,
  totalElements,
  onPageChange,
  onRowsPerPageChange,
  onCusipClick,
  onCustomizeColumns, // <-- add this prop
}) => {
  const { token } = useAuth();
  const {
    selectedPositionsData,
    selectedBondsTypeCount,
    togglePositionSelection,
    selectAllPositions,
    clearPositionSelection,
    selectedBondType,
    setSelectedBondType,
    selectedSecurityCharge,
    setSelectedSecurityCharge,
    // NEW: get virtual row from store
    virtualZeroPosition,
    clearVirtualZeroPosition, // NEW
  } = usePortfolioRebalanceStore();

  // State for bond type options
  const [bondTypeOptions, setBondTypeOptions] = useState([]);
  const [bondTypeLoading, setBondTypeLoading] = useState(false);
  const [securityChargeOptions, setSecurityChargeOptions] = useState([]);
  const [securityChargeLoading, setSecurityChargeLoading] = useState(false);

  // Add state for Zero Position Entry modal
  const [zeroEntryOpen, setZeroEntryOpen] = useState(false);

  // Fetch bond type options on component mount
  useEffect(() => {
    const fetchBondTypes = async () => {
      if (!token) return;

      setBondTypeLoading(true);
      try {
        const bondTypes = await getBondTypeOptions(token);
        setBondTypeOptions(orderBondTypeOptions(bondTypes));
      } catch (error) {
        console.error("Error fetching bond types:", error);
        // Fallback to static options if API fails
        setBondTypeOptions([]);
      } finally {
        setBondTypeLoading(false);
      }
    };

    fetchBondTypes();
  }, [token]);

  // Fetch security charge options on component mount
  useEffect(() => {
    const fetchSecurityCharges = async () => {
      if (!token) return;

      setSecurityChargeLoading(true);
      try {
        const securityCharges = await getSecurityChargeOptions(token);
        setSecurityChargeOptions(securityCharges || []);
      } catch (error) {
        console.error("Error fetching security charges:", error);
        setSecurityChargeOptions([]);
      } finally {
        setSecurityChargeLoading(false);
      }
    };

    fetchSecurityCharges();
  }, [token]);

  const handleRowClick = (rowIndex, rowData) => {
    console.log("Clicked row:", rowIndex, rowData);
  };

  const handleViewJson = (rowData) => {
    console.log("View JSON for:", rowData);
  };

  const displayData = useMemo(() => {
    // Always place virtual row at top if present
    if (virtualZeroPosition) {
      // Avoid accidental duplication (shouldn't exist in server data)
      return [virtualZeroPosition, ...(Array.isArray(data) ? data : [])];
    }
    return data;
  }, [data, virtualZeroPosition]);

  // Remove checkbox from enhanced columns since it's now handled separately in BondTable
  const enhancedSelectedColumns = selectedColumns;

  // Add handler for bond type change
  const handleBondTypeChange = (event) => {
    setSelectedBondType(event.target.value);
  };

  const handleSecurityChargeChange = (event) => {
    setSelectedSecurityCharge(event.target.value);
  };

  // Add handler for bond type pill clicks
  const handleBondTypePillClick = (bondType) => {
    setSelectedBondType(bondType);
  };

  // Helper function to get bond type display name
  const getBondTypeDisplayName = (bondType) => {
    switch (bondType.toLowerCase()) {
      case "gov":
      case "government":
      case "treasury":
        return "Treasury Bonds";
      case "corp":
      case "corporate":
        return "Corporate Bonds";
      case "muni":
      case "municipal":
        return "Municipal Bonds";
      default:
        return bondType;
    }
  };

  // Always "Add Security" per new requirement
  const addSecurityButtonLabel = "Add Security";
  const hasVirtualPosition = !!virtualZeroPosition;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 320px)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
            borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced hardcoded rgba
            flexShrink: 0,
          }}
        >
          {/* Bond Type Filter and Count Pills */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <Select
              value={selectedBondType}
              onChange={handleBondTypeChange}
              displayEmpty
              disabled={bondTypeLoading}
              sx={{
                width: "170px",
                height: "30px",
                borderRadius: "8px", // follow 8px radius rule
                border: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced
                color:
                  selectedBondType === "All"
                    ? tokens.colors.fontColour25 // replaced
                    : tokens.colors.fontColour, // replaced
                fontSize: "14px",
                fontWeight: "normal",
                "& .MuiSelect-select": { padding: "8px 16px" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
              }}
            >
              <MenuItem value="All">
                {bondTypeLoading ? "Loading..." : "All Bonds"}
              </MenuItem>
              {bondTypeOptions.map((bondType) => (
                <MenuItem key={bondType} value={bondType}>
                  {bondType}
                </MenuItem>
              ))}
            </Select>

            <Select
              value={selectedSecurityCharge}
              onChange={handleSecurityChargeChange}
              displayEmpty
              disabled={securityChargeLoading}
              sx={{
                width: "190px",
                height: "30px",
                borderRadius: "8px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                color:
                  selectedSecurityCharge === "All"
                    ? tokens.colors.fontColour25
                    : tokens.colors.fontColour,
                fontSize: "14px",
                fontWeight: "normal",
                "& .MuiSelect-select": { padding: "8px 16px" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
              }}
            >
              <MenuItem value="All">
                {securityChargeLoading ? "Loading..." : "All Factored Securities"}
              </MenuItem>
              {securityChargeOptions.map((securityCharge) => (
                <MenuItem key={securityCharge} value={securityCharge}>
                  {securityCharge}
                </MenuItem>
              ))}
            </Select>

            {/* Vertical Separator */}
            {selectedBondsTypeCount.total > 0 && (
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  mx: 0,
                  backgroundColor: tokens.colors.bodyBorderColor50, // replaced
                  height: "24px",
                  alignSelf: "center",
                }}
              />
            )}

            {/* Total selected count */}
            {selectedBondsTypeCount.total > 0 && (
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: tokens.colors.fontColour,
                  whiteSpace: "nowrap",
                }}
              >
                <Box component="span" sx={{ color: tokens.palette.primary }}>
                  {selectedBondsTypeCount.total}
                </Box>{" "}
                Bonds Selected
              </Typography>
            )}

            {/* Bond Type Count Pills */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {selectedBondsTypeCount.Gov > 0 && (
                <Box
                  sx={{
                    backgroundColor: tokens.colors.bodyHoverBackgroundColor50, // replaced
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced
                    borderRadius: "2px",
                    padding: "4px 8px",
                    color: tokens.colors.fontColour, // replaced
                    fontWeight: "bold",
                    fontSize: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: tokens.colors.bodyHoverBackgroundColor, // replaced
                    },
                  }}
                  onClick={() => handleBondTypePillClick("Gov")}
                >
                  {getBondTypeDisplayName("Gov")} ({selectedBondsTypeCount.Gov})
                </Box>
              )}
              {selectedBondsTypeCount.Corp > 0 && (
                <Box
                  sx={{
                    backgroundColor: tokens.colors.bodyHoverBackgroundColor50, // replaced
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced
                    borderRadius: "2px",
                    padding: "4px 8px",
                    color: tokens.colors.fontColour, // replaced
                    fontWeight: "bold",
                    fontSize: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: tokens.colors.bodyHoverBackgroundColor, // replaced
                    },
                  }}
                  onClick={() => handleBondTypePillClick("Corp")}
                >
                  {getBondTypeDisplayName("Corp")} (
                  {selectedBondsTypeCount.Corp})
                </Box>
              )}
              {selectedBondsTypeCount.Muni > 0 && (
                <Box
                  sx={{
                    backgroundColor: tokens.colors.bodyHoverBackgroundColor50, // replaced
                    border: `1px solid ${tokens.colors.bodyBorderColor50}`, // replaced
                    borderRadius: "2px",
                    padding: "4px 8px",
                    color: tokens.colors.fontColour, // replaced
                    fontWeight: "bold",
                    fontSize: "12px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: tokens.colors.bodyHoverBackgroundColor, // replaced
                    },
                  }}
                  onClick={() => handleBondTypePillClick("Muni")}
                >
                  {getBondTypeDisplayName("Muni")} (
                  {selectedBondsTypeCount.Muni})
                </Box>
              )}
            </Box>
          </Box>
          {/* Columns Button and Add Security Button */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<AddIcon />}
              onClick={() => setZeroEntryOpen(true)}
              disabled={hasVirtualPosition} // NEW disable when virtual exists
              sx={{ height: 42, textTransform: "none", borderRadius: '8px', opacity: hasVirtualPosition ? 0.6 : 1 }} // 8px radius
            >
              {addSecurityButtonLabel}
            </Button>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<ViewColumnOutlinedIcon />}
              sx={{ height: 42, textTransform: "none", borderRadius: '8px' }} // 8px radius
              onClick={(e) =>
                onCustomizeColumns && onCustomizeColumns(e.currentTarget)
              }
            >
              Columns
            </Button>
          </Box>
        </Box>

        {/* Table content */}
        <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
          <BondTable
            selectedColumns={enhancedSelectedColumns}
            filteredData={displayData}
            handleRowClick={handleRowClick}
            handleViewJson={handleViewJson}
            sectionKey="portfolio-rebalance"
            source="portfolio-rebalance"
            onSort={onSort}
            sortConfig={sortConfig}
            loading={loading}
            handleCusipClick={onCusipClick}
            selectedRows={selectedPositionsData}
            onRowSelectionChange={togglePositionSelection}
            onSelectAll={selectAllPositions}
            onClearSelection={clearPositionSelection}
            showCheckboxes={true}
            onEditVirtualZeroPosition={() => setZeroEntryOpen(true)}
            onDeleteVirtualZeroPosition={() => {
              clearVirtualZeroPosition();
            }} // NEW
          />
        </Box>
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: -25,
          width: "100%",
          color: tokens.colors.fontColour, // replaced white
          zIndex: 1000,
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <TablePagination
          component="footer"
          sx={{
            ".MuiSelect-select": { p: "4px 24px 5px 8px !important" },
          }}
          showFirstButton
          showLastButton
          rowsPerPage={itemsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          page={page}
          onRowsPerPageChange={onRowsPerPageChange}
          onPageChange={onPageChange}
          count={totalElements}
          labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`}
        />
      </Box>

      {/* Zero Position Entry Modal */}
      <ZeroPositionEntryModal
        open={zeroEntryOpen}
        onClose={() => setZeroEntryOpen(false)}
      />
    </>
  );
};

export default PortfolioRebalanceTable;
